import { ArrowDownTrayIcon, ExclamationTriangleIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import LegacyDocumentResponseResource from "common/resources/LegacyHistory/LegacyDocumentResponseResource";
import LegacyHistoryResponseResource from "common/resources/LegacyHistory/LegacyHistoryResponseResource";
import LegacyTransactionResponseResource from "common/resources/LegacyHistory/LegacyTransactionResponseResource";
import Button, { EButtonVariant } from "components/elements/Button";
import MessageBox from "components/elements/MessageBox";
import Typography, { ITypo, ITypoComponent, ITypoColor } from "components/elements/Typography";
import ApplicationConfig from "configs/ApplicationConfig";
import useHead from "hooks/useHead";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import ClientService from "services/ClientService";
import CorporationService from "services/CorporationService";
import { container } from "tsyringe";

import classes from "./classes.module.scss";

type Props = {
	entityType: "client" | "corporation";
};

const clientService = container.resolve(ClientService);
const corporationService = container.resolve(CorporationService);
const apiRoot = container.resolve(ApplicationConfig).get().api.rootUrl;
const currencyFormatter = new Intl.NumberFormat("fr-FR", {
	style: "currency",
	currency: "EUR",
});
const numberFormatter = new Intl.NumberFormat("fr-FR", {
	maximumFractionDigits: 4,
});
const dateFormatter = new Intl.DateTimeFormat("fr-FR");

const legacyLabels: Record<string, string> = {
	agreementElectronicDocument: "Accord de dématérialisation",
	legalCapacity: "Capacité juridique",
	birthDepartment: "Département de naissance",
	completeBeneficialOwners: "Bénéficiaires effectifs complets",
	completeLegalRepresentative: "Représentant légal complet",
	nif: "Numéro d’identification fiscale",
	taxResidenceCountry: "Pays de résidence fiscale",
	addressCountry: "Pays de l’adresse",
	birthDate: "Date de naissance",
	birthPlace: "Lieu de naissance",
	address: "Adresse",
	birthCountry: "Pays de naissance",
	city: "Ville",
	nationality: "Nationalité",
	zipCode: "Code postal",
	civility: "Civilité",
	email: "E-mail",
	siret: "SIRET",
	formeJuridique: "Forme juridique",
	regimeFiscal: "Régime fiscal",
	co_souscription_a_qualifier: "Co-souscription à qualifier",
	homonyme_existant_dans_le_cabinet: "Homonyme déjà présent dans le cabinet",
	missing_legal_representative: "Représentant légal manquant",
	missing_financial_row: "Données financières manquantes",
	missing_beneficial_owner: "Bénéficiaire effectif manquant",
	missing_company_row: "Données société manquantes",
	annual_income: "Revenu annuel",
	bic: "BIC",
	client_id: "Identifiant client source",
	dependents: "Personnes à charge",
	employment_status: "Situation professionnelle",
	fiscal_number: "Numéro fiscal",
	hosted_free: "Hébergé à titre gratuit",
	iban: "IBAN",
	ifi_tax: "Montant de l’IFI",
	invested_more_than_100k: "Investissement supérieur à 100 000 €",
	marginal_tax_threshold: "Tranche marginale d’imposition",
	monthly_expenses: "Charges mensuelles",
	non_real_estate_assets: "Patrimoine mobilier",
	profession: "Profession",
	real_estate_assets: "Patrimoine immobilier",
	risk_tolerance: "Tolérance au risque",
	scpi_assets: "Patrimoine SCPI",
	tax_regime: "Régime fiscal",
	tax_residence_country: "Pays de résidence fiscale",
	"proof of funds": "Justificatif de provenance des fonds",
	"contract documents": "Documents contractuels",
	"identity front": "Pièce d’identité – recto",
	"bank info": "RIB",
	"proof of address": "Justificatif de domicile",
	"identity back": "Pièce d’identité – verso",
	"company last balance sheet": "Dernier bilan de la société",
	"company status": "Statuts de la société",
	kbis: "Extrait Kbis",
	dbe: "Déclaration des bénéficiaires effectifs",
	"accommodation certificate": "Attestation d’hébergement",
	proof_of_funds: "Justificatifs de provenance des fonds",
	contract: "Documents contractuels",
	identity: "Pièces d’identité",
	kyc: "Documents de connaissance client",
	bank_details: "Coordonnées bancaires",
	corporation_kyc: "Documents de connaissance société",
	zero_byte_source_file: "fichier source vide",
	"invalid_source_value:not null": "valeur source invalide",
};

// Seules les opérations payées ou confirmées sont reprises et affichées.
const sourceStatusLabels: Record<string, string> = {
	paid: "Payée",
	confirmed: "Confirmée",
};

export default function LegacyHistory({ entityType }: Props) {
	const { clientId, corporationId } = useParams();
	const entityId = entityType === "client" ? clientId : corporationId;
	const [history, setHistory] = useState<LegacyHistoryResponseResource | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);

	useHead({ title: "Historique prestataire" });

	useEffect(() => {
		let active = true;
		if (!entityId) {
			setHasError(true);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		setHasError(false);
		const request = entityType === "client" ? clientService.getLegacyHistory(entityId) : corporationService.getLegacyHistory(entityId);
		request
			.then((response) => {
				if (active) setHistory(response);
			})
			.catch(() => {
				if (active) setHasError(true);
			})
			.finally(() => {
				if (active) setIsLoading(false);
			});

		return () => {
			active = false;
		};
	}, [entityId, entityType]);

	const hasLegacyData = useMemo(
		() => Boolean(history && (history.profiles.length || history.transactions.length || history.documents.length || history.anomalies.length)),
		[history],
	);

	if (isLoading) return <Typography typo={ITypo.P_MEDIUM}>Chargement de l’historique…</Typography>;
	if (hasError) return <MessageBox type="warning" text="Cet historique n’est pas disponible ou vous n’êtes pas autorisé à le consulter." />;
	if (!history || !hasLegacyData) return <MessageBox type="info" text="Aucun historique prestataire n’est associé à ce dossier." />;

	return (
		<div className={classes["root"]}>
			<header className={classes["header"]}>
				<div>
					<Typography typo={ITypo.H2} component={ITypoComponent.H2}>
						Historique prestataire
					</Typography>
					<Typography typo={ITypo.P_SMALL} color={ITypoColor.WILD_SAND_600} component={ITypoComponent.P}>
						Données reprises de {history.provider}, conservées en lecture seule.
					</Typography>
				</div>
				<span className={classes["readonly-badge"]}>
					<LockClosedIcon /> Lecture seule
				</span>
			</header>

			{(history.missingFields.length > 0 || history.anomalies.length > 0) && (
				<section className={classes["alert-section"]}>
					<MessageBox type="warning" text="Ce dossier historique comporte des informations à compléter ou à contrôler.">
						{history.missingFields.length > 0 && <TagList title="Champs manquants" values={history.missingFields} />}
						{history.anomalies.length > 0 && <TagList title="Anomalies" values={history.anomalies} />}
					</MessageBox>
				</section>
			)}

			{history.profiles.length > 0 && (
				<section className={classes["section"]}>
					<Typography typo={ITypo.P_LARGE_BOLD} component={ITypoComponent.H3}>
						Profil financier historique
					</Typography>
					{history.profiles.map((profile, profileIndex) => (
						<div className={classes["profile"]} key={`${profile.sourceCreatedAt ?? "profile"}-${profileIndex}`}>
							{profile.sourceCreatedAt && <div className={classes["muted"]}>Situation au {formatDate(profile.sourceCreatedAt)}</div>}
							{profile.financialData && Object.keys(profile.financialData).length > 0 ? (
								<dl className={classes["definition-grid"]}>
									{Object.entries(profile.financialData).map(([key, value]) => (
										<div key={key}>
											<dt>{legacyLabel(key)}</dt>
											<dd>{formatValue(value)}</dd>
										</div>
									))}
								</dl>
							) : (
								<div className={classes["muted"]}>Aucune donnée financière structurée.</div>
							)}
						</div>
					))}
				</section>
			)}

			<section className={classes["section"]}>
				<Typography typo={ITypo.P_LARGE_BOLD} component={ITypoComponent.H3}>
					Opérations reprises ({history.transactions.length})
				</Typography>
				{history.transactions.length === 0 ? (
					<span className={classes["muted"]}>Aucune opération reprise.</span>
				) : (
					<div className={classes["transactions"]}>
						{history.transactions.map((transaction) => (
							<Transaction key={transaction.id} transaction={transaction} />
						))}
					</div>
				)}
			</section>

			<section className={classes["section"]}>
				<Typography typo={ITypo.P_LARGE_BOLD} component={ITypoComponent.H3}>
					Documents du dossier ({history.documents.length})
				</Typography>
				<DocumentList documents={history.documents} />
			</section>
		</div>
	);
}

function Transaction({ transaction }: { transaction: LegacyTransactionResponseResource }) {
	return (
		<article className={classes["transaction"]}>
			<div className={classes["transaction-heading"]}>
				<div>
					<strong>{transaction.sourceProductLabel}</strong>
					<span className={classes["muted"]}>Référence source : {transaction.sourceId}</span>
				</div>
				<span className={classes[transaction.isArchived ? "status-archived" : "status-locked"]}>
					{transaction.isReadOnly && <LockClosedIcon />}
					{`${sourceStatusLabel(transaction.sourceStatus)} · ${transaction.isArchived ? "dossier archivé" : "verrouillée"}`}
				</span>
			</div>
			<dl className={classes["transaction-values"]}>
				<div>
					<dt>Date</dt>
					<dd>{formatDate(transaction.paymentDate ?? transaction.confirmationDate ?? transaction.signatureDate ?? transaction.sourceCreatedAt)}</dd>
				</div>
				<div>
					<dt>Démembrement</dt>
					<dd>{ownershipLabel(transaction.ownershipType)}</dd>
				</div>
				{transaction.ownershipType !== "PP" && (
					<>
						<div>
							<dt>Durée du démembrement</dt>
							<dd>{demembrementDurationLabel(transaction)}</dd>
						</div>
						{transaction.demembrementEndDate && (
							<div>
								<dt>Fin du démembrement</dt>
								<dd>{formatDate(transaction.demembrementEndDate)}</dd>
							</div>
						)}
						<div>
							<dt>Clé de démembrement source</dt>
							<dd>
								{transaction.partitionKey == null
									? "Non renseignée"
									: new Intl.NumberFormat("fr-FR", { style: "percent", maximumFractionDigits: 8 }).format(transaction.partitionKey)}
							</dd>
						</div>
					</>
				)}
				<div>
					<dt>Parts</dt>
					<dd>{numberFormatter.format(transaction.shares)}</dd>
				</div>
				<div>
					<dt>Prix unitaire source</dt>
					<dd>{transaction.unitPrice == null ? "Non renseigné" : currencyFormatter.format(transaction.unitPrice)}</dd>
				</div>
				<div>
					<dt>Montant source</dt>
					<dd>{currencyFormatter.format(transaction.amount)}</dd>
				</div>
				<div>
					<dt>Traitement Wenimmo</dt>
					<dd>{transaction.isArchived ? "Souscription archivée" : transaction.isOperational ? "Souscription associée" : "Historique uniquement"}</dd>
				</div>
			</dl>
			{transaction.hasCosubscriberProvenance && (
				<div className={classes["provenance"]}>
					<ExclamationTriangleIcon /> Une provenance co-souscripteur est conservée dans l’archive, sans création d’un conjoint.
				</div>
			)}
			{transaction.documents.length > 0 && (
				<div className={classes["transaction-documents"]}>
					<strong>Documents de l’opération</strong>
					<DocumentList documents={transaction.documents} />
				</div>
			)}
		</article>
	);
}

function demembrementDurationLabel(transaction: LegacyTransactionResponseResource): string {
	if (transaction.demembrementType === "viager") return "Viager";
	if (transaction.duration == null) return "Non renseignée";
	return `${transaction.duration} an${transaction.duration > 1 ? "s" : ""}`;
}

function DocumentList({ documents }: { documents: LegacyDocumentResponseResource[] }) {
	if (documents.length === 0) return <span className={classes["muted"]}>Aucun document historique.</span>;
	return (
		<ul className={classes["documents"]}>
			{documents.map((document) => (
				<li key={document.id}>
					<div>
						<strong>{legacyLabel(document.label)}</strong>
						<span className={classes["muted"]}>
							{legacyLabel(document.category)}
							{document.fileType ? ` · ${document.fileType}` : ""}
							{document.byteSize !== null ? ` · ${formatBytes(document.byteSize)}` : ""}
						</span>
					</div>
					{document.status === "available" && document.downloadPath ? (
						<Button
							variant={EButtonVariant.OUTLINED}
							icon={<ArrowDownTrayIcon />}
							onClick={() => window.open(`${apiRoot}${document.downloadPath}`, "_blank", "noopener,noreferrer")}>
							Télécharger
						</Button>
					) : (
						<span className={classes["missing-document"]}>
							Manquant
							{document.missingReason ? ` · ${legacyLabel(document.missingReason)}` : ""}
						</span>
					)}
				</li>
			))}
		</ul>
	);
}

function TagList({ title, values }: { title: string; values: string[] }) {
	return (
		<div className={classes["tag-group"]}>
			<strong>{title}</strong>
			<div>
				{values.map((value) => (
					<span key={value}>{legacyLabel(value)}</span>
				))}
			</div>
		</div>
	);
}

function humanize(value: string): string {
	return value
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.replace(/[_:.-]+/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

function legacyLabel(value: string): string {
	const translated = legacyLabels[value];
	if (translated) return translated;
	const missingDocumentPrefix = "missing_document:";
	if (value.startsWith(missingDocumentPrefix)) {
		return `Document manquant : ${legacyLabel(value.slice(missingDocumentPrefix.length))}`;
	}
	return humanize(value);
}

function sourceStatusLabel(value: string): string {
	return sourceStatusLabels[value] ?? legacyLabel(value);
}

function ownershipLabel(value: LegacyTransactionResponseResource["ownershipType"]): string {
	return value === "NP" ? "Nue-propriété" : value === "UF" ? "Usufruit" : "Pleine propriété";
}

function formatDate(value: Date | string | null): string {
	if (!value) return "Non renseignée";
	const date = new Date(value);
	return Number.isNaN(date.valueOf()) ? "Non renseignée" : dateFormatter.format(date);
}

function formatValue(value: unknown): string {
	if (value === null || value === undefined || value === "") return "Non renseigné";
	if (typeof value === "boolean") return value ? "Oui" : "Non";
	if (Array.isArray(value)) return value.map(formatValue).join(", ");
	if (typeof value === "object")
		return Object.entries(value as Record<string, unknown>)
			.map(([key, nested]) => `${legacyLabel(key)} : ${formatValue(nested)}`)
			.join(" · ");
	return String(value);
}

function formatBytes(value: number): string {
	if (value < 1024) return `${value} o`;
	if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} Kio`;
	return `${(value / (1024 * 1024)).toFixed(1)} Mio`;
}
